import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { dataSourceVersionAtom, selectedWIPAtom, selectedVersionAtom } from '../store/company';
import moment from 'moment';

const VarianceWeekSelector = ({ selectedWeeks, onChange }) => {
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [showDropdown, setShowDropdown] = useState(false);

    // Build sorted version list (most recent first), excluding current version
    const versionOptions = useMemo(() => {
        if (!versions || !versions.length) return [];
        const allowed = ['WIP', 'Regular', 'Reg-locked', 'Unassigned', 'WIP-locked'];
        return versions
            .filter(v => allowed.includes(v.Version_Type) && v.Version_GUID !== selectedVersion)
            .map(v => ({
                guid: v.Version_GUID,
                label: moment(v.Version_Start_DateTime).format('MMM D, YYYY') +
                    (v.Version_Type.includes('WIP') ? ' - WIP' : ''),
                date: v.Version_Start_DateTime,
                type: v.Version_Type,
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [versions, selectedVersion]);

    // Determine WIP week info
    const wipInfo = useMemo(() => {
        if (!selectedWIP) return null;
        return {
            guid: selectedWIP.Version_GUID,
            label: 'WIP ' + moment(selectedWIP.Version_Start_DateTime).format('MMM D'),
            date: selectedWIP.Version_Start_DateTime,
        };
    }, [selectedWIP]);

    // Determine "last week" — the most recent version before the current selected version
    const lastWeekInfo = useMemo(() => {
        if (!versions || !versions.length || !selectedVersion) return null;
        const currentVer = versions.find(v => v.Version_GUID === selectedVersion);
        if (!currentVer) return null;
        const currentDate = moment(currentVer.Version_Start_DateTime);
        const candidate = versionOptions.find(v =>
            v.guid !== selectedVersion &&
            v.guid !== wipInfo?.guid &&
            moment(v.date).isBefore(currentDate)
        );
        return candidate || null;
    }, [versions, selectedVersion, wipInfo, versionOptions]);

    // Build the default selections on first render
    const effectiveWeeks = useMemo(() => {
        if (selectedWeeks && selectedWeeks.length > 0) return selectedWeeks;
        const defaults = [];
        if (wipInfo) defaults.push(wipInfo.guid);
        return defaults;
    }, [selectedWeeks, wipInfo, lastWeekInfo]);

    // Propagate default weeks to parent on mount
    useEffect(() => {
        if ((!selectedWeeks || selectedWeeks.length === 0) && effectiveWeeks.length > 0) {
            onChange(effectiveWeeks);
        }
    }, [effectiveWeeks]);

    const lookupLabel = (guid) => {
        if (wipInfo && guid === wipInfo.guid) return wipInfo.label;
        const found = versionOptions.find(v => v.guid === guid);
        return found ? found.label : guid;
    };

    const removeWeek = (guid) => {
        onChange(effectiveWeeks.filter(g => g !== guid));
    };

    const addWeek = (guid) => {
        if (!effectiveWeeks.includes(guid)) {
            onChange([...effectiveWeeks, guid]);
        }
        setShowDropdown(false);
    };

    const unusedVersions = versionOptions.filter(v => !effectiveWeeks.includes(v.guid));

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 12, fontWeight: '700', color: '#475569', letterSpacing: 0.3 }}>Variance Weeks:</Text>
            {effectiveWeeks.map(guid => (
                <View key={guid} style={{
                    flexDirection: 'row', alignItems: 'center', gap: 4,
                    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '3px 8px',
                }}>
                    <Text style={{ fontSize: 11, color: '#166534', fontWeight: '500' }}>{lookupLabel(guid)}</Text>
                    <Pressable onPress={() => removeWeek(guid)} style={{ cursor: 'pointer' }}>
                        <Text style={{ fontSize: 13, color: '#86efac', fontWeight: 'bold' }}>×</Text>
                    </Pressable>
                </View>
            ))}
            {unusedVersions.length > 0 && (
                <View style={{ position: 'relative' }}>
                    <Pressable
                        onPress={() => setShowDropdown(!showDropdown)}
                        style={{ cursor: 'pointer', backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: 4, border: '1px solid #bfdbfe' }}
                    >
                        <Text style={{ fontSize: 12, color: '#2563eb', fontWeight: '600' }}>+ Week</Text>
                    </Pressable>
                    {showDropdown && (
                        <View style={{
                            position: 'absolute', top: 28, left: 0, zIndex: 1000,
                            backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6,
                            shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 6,
                            maxHeight: 200, overflowY: 'auto', minWidth: 200,
                        }}>
                            {unusedVersions.map(v => (
                                <Pressable
                                    key={v.guid}
                                    onPress={() => addWeek(v.guid)}
                                    style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                                >
                                    <Text style={{ fontSize: 12, color: '#334155' }}>{v.label}</Text>
                                </Pressable>
                            ))}
                        </View>
                    )}
                </View>
            )}
        </View>
    );
};

export default VarianceWeekSelector;
